import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  X,
  MessageSquare,
  User,
  Building,
  Clock,
  ExternalLink,
  UserPlus,
  CheckCircle,
  Loader2,
  Headphones,
  Bell,
} from "lucide-react";


/**
 * Component that displays popup notifications when new support requests arrive.
 * Staff can take the request themselves or assign to another staff member.
 */
export function SupportRequestNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const { token, getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const audioRef = useRef(null);

  // Check if user is staff
  const isStaff = user?.user_type === "europacific_staff" || user?.is_admin || user?.role === "admin";

  // Fetch notifications on mount
  useEffect(() => {
    if (!token || !isStaff) return;

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/live-support/notifications`, {
          headers: getAuthHeaders(),
        });
        setNotifications(response.data.notifications || []);
      } catch (error) {
        console.error("Failed to fetch support notifications:", error);
      }
    };

    fetchNotifications();

    // Connect to WebSocket for real-time notifications
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${API_URL.replace(/^https?:/, wsProtocol + ':')}/live-support/ws/staff?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === "new_support_request") {
          // Play notification sound
          playNotificationSound();
          
          // Add to notifications
          setNotifications(prev => [{
            id: data.notification_id || `temp-${Date.now()}`,
            request_id: data.request_id,
            customer_name: data.customer_name,
            customer_email: data.customer_email,
            company: data.company,
            summary: data.summary,
            created_at: data.created_at,
            priority: data.priority
          }, ...prev.slice(0, 4)]); // Keep max 5 notifications
        }
        
        if (data.type === "support_request_taken") {
          // Remove notification that was taken
          setNotifications(prev => 
            prev.filter(n => n.request_id !== data.request_id)
          );
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error("Support WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to connect to support WebSocket:", error);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token, isStaff, getAuthHeaders]);

  const playNotificationSound = () => {
    try {
      // Create audio context for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
      
      // Second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        osc2.connect(gainNode);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        osc2.start();
        osc2.stop(audioContext.currentTime + 0.15);
      }, 250);
    } catch (e) {
      console.log("Could not play notification sound");
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axios.post(
        `${API_URL}/live-support/notifications/${notificationId}/dismiss`,
        {},
        { headers: getAuthHeaders() }
      );
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    }
  };

  const handleTakeRequest = async (notification) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/live-support/notifications/${notification.id}/take`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success("You have taken this support request");
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      
      // Navigate to support queue with the session open
      navigate(`/support-queue?session=${response.data.request_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to take request");
    } finally {
      setLoading(false);
    }
  };

  const openAssignModal = async (notification) => {
    setSelectedNotification(notification);
    setShowAssignModal(true);
    setSelectedStaff("");
    
    // Fetch staff list
    setLoadingStaff(true);
    try {
      const response = await axios.get(`${API_URL}/live-support/staff-list`, {
        headers: getAuthHeaders()
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      toast.error("Failed to load staff list");
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleAssignRequest = async () => {
    if (!selectedStaff || !selectedNotification) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/live-support/notifications/${selectedNotification.id}/assign`,
        { staff_id: selectedStaff },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Request assigned to ${response.data.assigned_to}`);
      setNotifications(prev => prev.filter(n => n.id !== selectedNotification.id));
      setShowAssignModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign request");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isStaff || notifications.length === 0) return null;

  return (
    <>
      {/* Notification Popups */}
      <div className="fixed top-20 right-4 z-[70] w-full max-w-md space-y-3">
        <AnimatePresence>
          {notifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 100, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 300, delay: index * 0.1 }}
              className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-2xl overflow-hidden"
              data-testid={`support-notification-${notification.id}`}
            >
              {/* Header */}
              <div className="px-4 py-3 flex items-center justify-between bg-black/10">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-white/20 rounded-lg">
                    <Headphones className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">New Support Request</span>
                  {notification.priority === "high" && (
                    <Badge className="bg-red-500 text-white text-xs">Urgent</Badge>
                  )}
                </div>
                <button
                  onClick={() => dismissNotification(notification.id)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 text-white/80" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 text-white">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-lg leading-tight">
                      {notification.customer_name || "Customer"}
                    </h4>
                    {notification.company && (
                      <p className="text-blue-100 text-sm flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        {notification.company}
                      </p>
                    )}
                  </div>
                  <span className="text-blue-100 text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTime(notification.created_at)}
                  </span>
                </div>

                {/* Summary */}
                <div className="bg-white/10 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-50 leading-relaxed">
                    "{notification.summary || "Customer requested support"}"
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleTakeRequest(notification)}
                    disabled={loading}
                    className="flex-1 bg-white text-blue-700 hover:bg-blue-50 font-semibold"
                    data-testid={`take-request-${notification.id}`}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Take It
                  </Button>
                  <Button
                    onClick={() => openAssignModal(notification)}
                    variant="outline"
                    className="flex-1 border-white/30 text-white hover:bg-white/10"
                    data-testid={`assign-request-${notification.id}`}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign
                  </Button>
                  <Button
                    onClick={() => {
                      navigate(`/support-queue?session=${notification.request_id}`);
                      dismissNotification(notification.id);
                    }}
                    variant="ghost"
                    className="text-white hover:bg-white/10"
                    title="View in Support Queue"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Assign Support Request
            </DialogTitle>
            <DialogDescription>
              Assign this request to a team member who can help the customer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedNotification && (
              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-sm font-medium">
                  {selectedNotification.customer_name}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  "{selectedNotification.summary}"
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To</label>
              {loadingStaff ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading staff...
                </div>
              ) : (
                <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.name}
                        {staff.department && (
                          <span className="text-muted-foreground ml-2">
                            ({staff.department})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignRequest} 
              disabled={!selectedStaff || loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
